import React, { useState, useEffect, Fragment } from 'react';
import { withRouter, Link } from 'react-router-dom';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import {
  Tabs, Table, notification, Popconfirm, Tag, Tooltip, Popover,
} from 'antd';
import dayjs from 'dayjs';
import {
  ArrowDownOutlined, ArrowUpOutlined, FilePdfOutlined, EditOutlined, ExclamationCircleOutlined, LoadingOutlined, UserOutlined, DownloadOutlined,
} from '@ant-design/icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRightLong } from '@fortawesome/free-solid-svg-icons';
import PriceListActions from '@Actions/inventory/priceListActions';
import H3Text from '@Uilib/h3Text';
import Helpers from '@Apis/helpers';
import './style.scss';
import Constants, { toISTDate } from '@Apis/constants';

const { TabPane } = Tabs;
const ViewPriceList = ({
  user, selectedPriceList, getPriceListById, selectedTenant, priceListId, getPriceListByIdLoading, MONEY, deletePriceListLoading, deletePriceList, callback, updatePriceListStatusLoading, updatePriceListStatus,
}) => {
  const [call, setCall] = useState(false);
  const [activeTab, setActiveTab] = useState('1');
  useEffect(() => {
    getPriceListById(selectedTenant || Helpers.getTenantEntityPermission(user?.user_tenants, Helpers.permissionEntities.PRODUCT, Helpers.permissionTypes.READ)?.join(','), priceListId, true, (data) => {
      setActiveTab(data?.price_list_type === 'SELECTED_ITEMS' ? '1' : '2');
    });
  }, [call]);

  const getPriceListColumns = () => {
    const priceListColumns = [
      {
        title: '#',
        responsive: ['sm', 'md', 'lg', 'xxl'],
        render: (record) => (
          <Link to={`/inventory/product/view/${record?.product_sku_id}`} target="_blank">
            {`#${record?.internal_sku_code}` || '-'}
          </Link>
        ),
      },
      {
        title: 'PRODUCT NAME',
        responsive: ['sm', 'md', 'lg', 'xxl'],
        render: (record) => (
          <div>
            {record?.product_sku_name || '-'}
          </div>
        ),
      },
      {
        title: 'PRICE',
        responsive: ['sm', 'md', 'lg', 'xxl'],
        render: (record) => (
          <div>
            {MONEY(record?.price_list_amount) || '-'}
          </div>
        ),
      },
      {
        title: 'DISCOUNT',
        responsive: ['sm', 'md', 'lg', 'xxl'],
        render: (record) => (
          <div>
            {`${Number(record?.discount_percentage) || 0} %`}
          </div>
        ),
      },
    ];
    if (selectedPriceList?.pricing_scheme === 'VOLUME_PRICING') {
      priceListColumns.push(
        {
          title: 'START QUANTITY',
          responsive: ['sm', 'md', 'lg', 'xxl'],
          render: (record) => (
            <div>
              {record?.start_quantity || '-'}
            </div>
          ),
        },
        {
          title: 'END QUANTITY',
          responsive: ['sm', 'md', 'lg', 'xxl'],
          render: (record) => (
            <div>
              {record?.end_quantity || '-'}
            </div>
          ),
        },
      );
    }
    return priceListColumns;
  };

  const getPricingRuleTextAndColor = (pricingRule) => {
    if (pricingRule === 'MARK_UP_PERCENTAGE') {
      return { text: 'Markup', color: 'green', arrow: <ArrowUpOutlined style={{ color: 'green' }} /> };
    } if (pricingRule === 'MARK_DOWN_PERCENTAGE') {
      return { text: 'Markdown', color: 'red', arrow: <ArrowDownOutlined style={{ color: 'red' }} /> };
    }
    return { text: '', color: 'black', arrow: null };
  };
  const { text, color, arrow } = getPricingRuleTextAndColor(selectedPriceList?.pricing_rule);

  const getDatesDiff = (fromDate, toDate) => {
    const today = dayjs();
    const diffInDays = dayjs(toDate).diff(dayjs(fromDate), 'day', false);

    if (diffInDays === 0) {
      return 'Today';
    }
    let diff = '';
    if (diffInDays > 360) {
      const years = Math.floor(diffInDays / 365);
      diff = `${years} Year${years > 1 ? 's' : ''}`;
    } else if (diffInDays > 180) {
      const months = Math.floor(diffInDays / 30);
      diff = `${months} Month${months > 1 ? 's' : ''}`;
    } else if (diffInDays > 28) {
      const weeks = Math.floor(diffInDays / 7);
      diff = `${weeks} Week${weeks > 1 ? 's' : ''}`;
    } else if (diffInDays > 0) {
      diff = `${diffInDays} Day${diffInDays > 1 ? 's' : ''}`;
    }
    return diff;
  };
  const getExpiryStatus = (expiryDate) => {
    const today = dayjs();
    const expiry = dayjs(expiryDate);
    const hoursDifference = expiry.diff(today, 'hour');

    if (hoursDifference > 24) {
      const daysDifference = Math.floor(hoursDifference / 24);
      if (daysDifference > 7) {
        const weeks = Math.floor(daysDifference / 7);
        return `Expires in ${weeks} week${weeks > 1 ? 's' : ''} `;
      }
      return `Expires in ${daysDifference} day${daysDifference > 1 ? 's' : ''}`;
    }

    if (hoursDifference === 0 || hoursDifference > 1 || hoursDifference > 0 || hoursDifference === -1) {
      return 'Expires Today';
    }

    if (hoursDifference >= -24) {
      const hoursAgo = Math.abs(hoursDifference);
      return `Expired ${hoursAgo} hour${hoursAgo > 1 ? 's' : ''} ago`;
    }

    const daysAgo = Math.abs(Math.floor(hoursDifference / 24));
    return `Expired ${daysAgo} day${daysAgo > 1 ? 's' : ''} ago`;
  };

  const columnsForSelectCustomers = [
    {
      title: 'CUSTOMER',
      key: 'customer_name',
      width: 220,
      render: (text, record) => (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '5px',
          }}
        >
          <H3Text
            text={(
              <React.Fragment>
                {record?.customer_info?.internal_cust_code || '-'}
              </React.Fragment>
            )}
            style={{ color: '#2D7DF7', fontWeight: '500' }}
          />
          <H3Text
            text={(
              <React.Fragment>
                {record?.customer_info?.customer_name ? record?.customer_info?.customer_name : '-'}
              </React.Fragment>
            )}
            style={{ fontWeight: '500' }}
          />
        </div>
      ),
      responsive: ['sm', 'md', 'lg', 'xxl'],
    },
    {
      title: 'CUSTOMER TYPE',
      key: 'customer_code',
      width: 100,
      render: (text, record) => `${record?.customer_info?.customer_type
        ? record?.customer_info?.customer_type
        : '-'
        }`,
      responsive: ['sm', 'md', 'lg', 'xxl'],
    },
    {
      title: 'SALES MANAGER',
      key: 'sales_manager',
      width: 120,
      render: (text, record) => (
        <React.Fragment>
          {record?.customer_info?.account_manager_info?.account_manager_id ? (`${record?.customer_info?.account_manager_info?.first_name} ${record?.customer_info?.account_manager_info?.last_name}`) : '-'}
        </React.Fragment>
      ),
      responsive: ['sm', 'md', 'lg', 'xxl'],
    },
  ];

  return (
    <Fragment>
        {
          (getPriceListByIdLoading || !selectedPriceList) ? (
            <div className="loader__container">
              <div className="loader__container-left">
                <div className="loading-header">
                  {[1, 2, 3, 4].map((item) => (
                    <div key={item} className="loading-header-row">
                      <div className="loading-header__label loadingBlock" />
                      <div className="loading-header__label loadingBlock" />
                    </div>
                  ))}

                </div>
                <div className="loading-address">
                  {[1, 2].map((item) => (<div key={item} className="address loadingBlock" />))}
                </div>
                <div className="loading-table loadingBlock" />
                <div className="loading-header loading-total">
                  {[1, 2, 3, 4].map((item) => (
                    <div key={item} className="loading-header-row">
                      <div className="loading-header__label loadingBlock" />
                      <div className="loading-header__label loadingBlock" />
                    </div>

                  ))}
                </div>
                <div className="loading-header loading-total">
                  <div className="loading-header-row">
                    <div className="loading-header__label loadingBlock" />
                    <div className="loading-header__label loadingBlock" />
                  </div>
                </div>
              </div>
              <div className="loader__container-right">
                {[1, 2, 3].map((item) => (
                  <div key={item} className="loader__container-right-row">
                    <div style={{ display: 'flex' }}>
                      <div className="loader__container-right-row__icon loadingBlock" />
                      <div>
                        <div className="loader__container-right-row__head loadingBlock" />
                        <div className="loader__container-right-row__sub-head loadingBlock" />
                        <div className="loader__container-right-row__sub-head loadingBlock" />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <Fragment>
              <div className="view-document__wrapper">
                <div className="ant-row">
                  <div className="ant-col-md-24 ant-col-xs-24">
                    <div className=" view-left__wrapper ">
                      <div className="view-document__title">
                        <div className="view-document__title-number-wrapper">
                          <div className="view-document__title-number" style={{ color: '#2D7DF7', fontSize: '16px' }}>
                            {`#${selectedPriceList?.price_list_doc_number} ${selectedPriceList?.price_list_name}`}
                          </div>
                          <div className="view-document__title-status" style={{ backgroundColor: selectedPriceList?.is_active ? '#22C55E' : '#cb351a' }}>
                            {selectedPriceList?.is_active ? 'ACTIVE' : 'INACTIVE'}
                          </div>
                        </div>

                        <div className="view-document__title-actions">
                          {selectedPriceList?.price_list_type === 'SELECTED_ITEMS' && (
                            <div className="action-buttons">
                              <H3Text
                                text={(
                                  <a
                                    target="_blank"
                                    href={`${Constants.PRICE_LIST}/download_id?price_list_id=${selectedPriceList?.price_list_id}&include_other_products=false&org_id=${user?.tenant_info?.org_id}`}
                                    rel="noreferrer"
                                  >
                                    <DownloadOutlined />
                                  </a>
                                )}
                                className="action-button"
                              />
                            </div>
                          )}

                          <div className="action-buttons">
                            {Helpers.getPermission(Helpers.permissionEntities.PRICE_LIST,
                              Helpers.permissionTypes.UPDATE, user) && !selectedPriceList?.is_expired && (
                                <Popconfirm
                                  placement="topRight"
                                  title={`Are you sure you want to ${selectedPriceList?.is_active ? 'deactivate' : 'activate'} this price list?`}
                                  onConfirm={() => {
                                    if (!selectedPriceList?.is_expired) {
                                      const payload = {
                                        price_list_ids: selectedPriceList?.price_list_id,
                                        status: !selectedPriceList?.is_active,
                                        is_expired: false,
                                      };
                                      updatePriceListStatus(payload, () => {
                                        setCall(!call);
                                      });
                                    }
                                  }}
                                  okText="Yes"
                                  cancelText="No"
                                >
                                  <H3Text
                                    text={(
                                      <div style={{ whiteSpace: 'nowrap' }}>
                                        {updatePriceListStatusLoading ? <LoadingOutlined /> : (
                                          <span>{`Mark as ${selectedPriceList?.is_active ? 'Inactive' : 'Active'}`}</span>
                                        )}
                                      </div>
                                    )}
                                    className="action-button action-button-big"
                                  />
                                </Popconfirm>
                              )}
                            {Helpers.getPermission(Helpers.permissionEntities.PRICE_LIST,
                              Helpers.permissionTypes.DELETE, user) && (
                                <Popconfirm
                                  placement="topRight"
                                  title="Are you sure you want to delete this price list?"
                                  onConfirm={() => {
                                    if (!deletePriceListLoading) {
                                      deletePriceList({ price_list_id: selectedPriceList?.price_list_id }, () => callback());
                                    }
                                  }}
                                  okText="Yes"
                                  cancelText="No"
                                >
                                  <H3Text
                                    text={(
                                      <div>
                                        {deletePriceListLoading ? <LoadingOutlined /> : (
                                          <span>Delete</span>
                                        )}
                                      </div>
                                    )}
                                    className="action-button action-button-big"
                                  />
                                </Popconfirm>
                              )}
                          </div>
                        </div>
                      </div>
                      <div className="document-header">
                        <div className="ant-row">
                          <div className="ant-col-md-12">
                            <div className="document-header__field">
                              <H3Text text="Price List Type" className="document-header__field-name" />
                              <H3Text
                                text={(
                                  <div>
                                    <span>{selectedPriceList?.price_list_type ? selectedPriceList?.price_list_type.split('_').join(' ').toProperCase() : '-'}</span>
                                    &nbsp;
                                    {arrow}
                                    <span style={{ color, fontSize: '11px', fontWeight: '600' }}>
                                      {text}
                                      {' '}
                                      {selectedPriceList?.pricing_rule_percentage != null
                                        ? `${Math.abs(selectedPriceList?.pricing_rule_percentage)}%`
                                        : ''}
                                    </span>
                                  </div>
                                )}
                                className="document-header__field-value"
                              />
                            </div>
                          </div>
                          <div className="ant-col-md-12">
                            <div className="document-header__field">
                              <H3Text text="Validity" className="document-header__field-name" />
                              <H3Text
                                text={(
                                  <div>
                                    {(selectedPriceList?.valid_from && selectedPriceList?.valid_to) ? (
                                      <Fragment>
                                        <span className="priceList__date-diff">
                                          {dayjs.utc(selectedPriceList?.valid_from).utcOffset('+05:30').format('DD/MM/YYYY')}
                                          &nbsp;
                                          <FontAwesomeIcon icon={faArrowRightLong} />
                                          &nbsp;
                                          {dayjs.utc(selectedPriceList?.valid_to).utcOffset('+05:30').format('DD/MM/YYYY')}
                                        </span>
                                      </Fragment>
                                    ) : '-'}
                                  </div>
                                )}
                                className="document-header__field-value"
                              />
                            </div>
                          </div>
                          <div className="ant-col-md-12">
                            <div className="document-header__field">
                              <H3Text text="Pricing Scheme" className="document-header__field-name" />
                              <H3Text
                                text={selectedPriceList?.pricing_scheme === 'VOLUME_PRICING' ? 'Slab Pricing' : (selectedPriceList?.pricing_scheme ? selectedPriceList?.pricing_scheme.split('_').join(' ').toProperCase() : '-')}
                                className="document-header__field-value"
                              />
                            </div>
                          </div>

                          {/* <div className="ant-col-md-12">
                            <div className="document-header__field">
                              <H3Text text="States" className="document-header__field-name" />
                              <H3Text
                                text={(
                                  <div>
                                    {selectedPriceList?.applicable_state?.slice(0, 2)?.map((state) => (
                                      <div
                                        key={state}
                                      >
                                        {state}
                                      </div>
                                    ))}
                                    {selectedPriceList?.applicable_state?.length > 2 && (
                                      <Popover
                                        content={(
                                          <div
                                            style={{ maxHeight: '100px', maxWidth: '250px', overflowY: 'auto' }}
                                          >
                                            <div
                                              style={{
                                                color: 'black', fontWeight: '600', fontSize: '13px',
                                              }}
                                            >
                                              Selected States
                                            </div>
                                            {selectedPriceList?.applicable_state?.slice(2)?.map((state) => (
                                              <div
                                                key={state}
                                              >
                                                <div
                                                  style={{
                                                    color: '#2D7DF7', fontWeight: '600', fontSize: '12px',
                                                  }}
                                                >
                                                  {state}
                                                </div>
                                              </div>
                                            ))}
                                          </div>
                                        )}
                                      >
                                        <div className="table-subscript" style={{ color: 'grey', cursor: 'pointer' }}>
                                          {`and ${selectedPriceList?.applicable_state.length - 2} others`}
                                        </div>
                                      </Popover>

                                    )}
                                  </div>
                                )}
                                className="document-header__field-value"
                              />
                            </div>
                          </div> */}
                          {/* <div className="ant-col-md-12">
                            <div className="document-header__field">
                              <H3Text text="Customers" className="document-header__field-name" />
                              <H3Text
                                text={(
                                  <div>
                                    {selectedPriceList?.customer_details?.slice(0, 2)?.map((item) => (
                                      <div key={item?.customer_id}>
                                        {item?.customer_name || '-'}
                                      </div>
                                    ))}
                                    {selectedPriceList?.customer_details?.length > 2 && (
                                      <Popover
                                        content={(
                                          <div
                                            style={{ maxHeight: '100px', maxWidth: '250px', overflowY: 'auto' }}
                                          >
                                            <div
                                              style={{
                                                color: 'black', fontWeight: '600', fontSize: '13px',
                                              }}
                                            >
                                              Selected Customers
                                            </div>
                                            {selectedPriceList?.customer_details?.slice(2)?.map((item) => (
                                              <div
                                                key={item?.customer_id}
                                              >
                                                <Link
                                                  to={`/sales/customer/view/${item?.customer_id}`}
                                                  target="_blank"
                                                  style={{
                                                    color: '#2D7DF7', fontWeight: '600', fontSize: '12px', cursor: 'pointer',
                                                  }}
                                                >
                                                  {item?.customer_name}
                                                </Link>
                                              </div>
                                            ))}
                                          </div>
                                        )}
                                      >
                                        <div className="table-subscript" style={{ color: 'grey', cursor: 'pointer' }}>
                                          {`and ${selectedPriceList?.customer_details.length - 2} others`}
                                        </div>
                                      </Popover>

                                    )}
                                  </div>
                                )}
                                className="document-header__field-value"
                              />
                            </div>
                          </div> */}
                          <div className="ant-col-md-12">
                            <div className="document-header__field">
                              <H3Text text="Locations" className="document-header__field-name" />
                              <H3Text
                                text={(
                                  <div>
                                    {selectedPriceList?.tenant_info?.slice(0, 2)?.map((item) => (
                                      <div
                                        key={item?.tenant_id}
                                      >
                                        {item?.tenant_name}
                                      </div>
                                    ))}
                                    {selectedPriceList?.tenant_info?.length > 2 && (
                                      <Popover
                                        content={(
                                          <div
                                            style={{ maxHeight: '90px', maxWidth: '280px', overflowY: 'auto' }}
                                          >
                                            <div
                                              style={{
                                                color: 'black', fontWeight: '600', fontSize: '13px',
                                              }}
                                            >
                                              Selected Locations
                                            </div>
                                            {selectedPriceList?.tenant_info?.slice(2)?.map((item) => (
                                              <div
                                                key={item?.tenant_id}
                                              >
                                                <Link
                                                  style={{
                                                    color: '#2D7DF7', fontWeight: '600', fontSize: '12px', cursor: 'pointer',
                                                  }}
                                                >
                                                  {item?.tenant_name}
                                                </Link>
                                              </div>
                                            ))}
                                          </div>
                                        )}
                                      >
                                        <div className="table-subscript" style={{ color: 'grey', cursor: 'pointer' }}>
                                          {`and ${selectedPriceList?.tenant_info?.length - 2} others`}
                                        </div>
                                      </Popover>

                                    )}
                                  </div>
                                )}
                                className="document-header__field-value"
                              />
                            </div>

                          </div>
                        </div>
                      </div>
                      <Tabs activeKey={activeTab} onChange={(key) => setActiveTab(key)}>
                        {selectedPriceList?.price_list_type === 'SELECTED_ITEMS' && (
                          <TabPane
                            tab="Products"
                            key="1"
                          >
                            <Table
                              title={() => (
                                <div>{`${selectedPriceList?.product_list?.length || 0} Products`}</div>
                              )}
                              size="small"
                              scroll="scroll"
                              columns={getPriceListColumns() || []}
                              dataSource={selectedPriceList?.product_list || []}
                              pagination={selectedPriceList?.product_list?.length > 15}
                              bordered
                            />

                          </TabPane>
                        )}
                        <TabPane
                          tab="Customers"
                          key="2"
                        >
                          <Table
                            columns={columnsForSelectCustomers}
                            scroll={{ y: 300 }}
                            pagination={false}
                            bordered={false}
                            size="small"
                            dataSource={selectedPriceList?.customer_details?.map((customer) => ({
                              key: customer?.customer_id,
                              customer_info: customer,
                            })) || []}
                            title={() => (
                              <H3Text
                                text={`${selectedPriceList && selectedPriceList?.customer_details?.length ? selectedPriceList?.customer_details?.length : '0'
                                  } Total Customers`}
                                className=""
                              />
                            )}
                          />
                        </TabPane>
                      </Tabs>
                    </div>
                  </div>
                </div>
              </div>
            </Fragment>
          )
        }
    </Fragment>
  );
};

const mapStateToProps = ({
  UserReducers, PriceListReducers,
}) => ({
  user: UserReducers.user,
  MONEY: UserReducers.MONEY,
  selectedPriceList: PriceListReducers.selectedPriceList,
  getPriceListsLoading: PriceListReducers.getPriceListsLoading,
  getPriceListByIdLoading: PriceListReducers.getPriceListByIdLoading,
  deletePriceListLoading: PriceListReducers.deletePriceListLoading,
  updatePriceListStatusLoading: PriceListReducers.updatePriceListStatusLoading,
});

const mapDispatchToProps = (dispatch) => ({
  getPriceListById: (tenantId, priceListId, isListScreen, callback) => dispatch(PriceListActions.getPriceListById(tenantId, priceListId, isListScreen, callback)),
  deletePriceList: (payload, callback) => dispatch(PriceListActions.deletePriceList(payload, callback)),
  updatePriceListStatus: (payload, callback) => dispatch(PriceListActions.updatePriceListStatus(payload, callback)),
});

ViewPriceList.propTypes = {
  getPriceListById: PropTypes.func,
  getPriceListByIdLoading: PropTypes.bool,
  selectedPriceList: PropTypes.any,
  user: PropTypes.any,
  history: PropTypes.func,
};

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(ViewPriceList));
